// Welcome / First Login Page strings — EN + TR.
//
// Wave-5 Day 1. WelcomePage (`features/onboarding/WelcomePage.tsx`) chrome
// localization. Page is post-invite-acceptance tour + checklist (NOT a
// marketing landing) — sessionStorage context handoff per BUG-11; 3-step
// role-aware stepper; getting-started checklist; dashboard CTA.
//
// Tour STEP CONTENT (title/body/highlight) lives in `tour-content.ts`
// (role-driven data). Checklist items also role-driven. This dict only
// covers the page CHROME — brand, fallback card, hero greeting, tour
// stepper labels, post-tour state, checklist header, footer.
//
// 🔴 SELLER-LEAK INVARIANT: NO vendor names (Mehmet/Hakan/Aybars/Furkan/
// Bursa) and NO "Supplier"/"Workshop". Audience-perspective wording in
// this dict is platform-neutral; tour-content.ts stays out of scope (V1
// driver-data sized; deferred to V1.5).

import type { LocalizedDict } from '../index'

type WelcomePageStrings = {
  brand: string
  fallback: {
    title: string
    description: string
    cta: string
  }
  hero: {
    greeting: (orgName: string) => string
  }
  tour: {
    sectionTitle: string
    stepCounter: (current: number, total: number) => string
    skipTour: string
    lookFor: string
    back: string
    next: string
    finishTour: string
  }
  tourComplete: {
    label: string
    description: string
    cta: string
  }
  checklist: {
    sectionTitle: string
    progress: (done: number, total: number) => string
    footnote: string
  }
  skipLink: string
  footer: string
}

export const WELCOME_PAGE_STRINGS: LocalizedDict<WelcomePageStrings> = {
  en: {
    brand: 'PEXIFY',
    fallback: {
      title: "You're in!",
      description:
        'Welcome to PEXIFY. Continue to your dashboard to get started.',
      cta: 'Continue to Dashboard',
    },
    hero: {
      greeting: (orgName) => `Welcome, ${orgName}!`,
    },
    tour: {
      sectionTitle: 'Quick tour',
      stepCounter: (current, total) => `Step ${current} of ${total}`,
      skipTour: 'Skip tour',
      lookFor: 'Look for:',
      back: 'Back',
      next: 'Next',
      finishTour: 'Finish tour',
    },
    tourComplete: {
      label: 'Tour complete',
      description: 'Ready to dive in? Your dashboard is one click away.',
      cta: 'Open Dashboard',
    },
    checklist: {
      sectionTitle: 'Getting started',
      progress: (done, total) => `${done}/${total}`,
      footnote:
        'Mark items as you go — this is a manual checklist (V1 simulation; Wave 2 auto-detects completion via real backend signals).',
    },
    skipLink: 'Or skip everything and go straight to your dashboard',
    footer: 'PEXIFY · privately governed commerce ecosystem',
  },
  tr: {
    brand: 'PEXIFY',
    fallback: {
      title: 'Hoş geldin!',
      description:
        "PEXIFY'a hoş geldin. Başlamak için dashboard'a geç.",
      cta: "Dashboard'a geç",
    },
    hero: {
      greeting: (orgName) => `Hoş geldin, ${orgName}!`,
    },
    tour: {
      sectionTitle: 'Hızlı tur',
      stepCounter: (current, total) => `Adım ${current} / ${total}`,
      skipTour: 'Turu atla',
      lookFor: 'Şuna bak:',
      back: 'Geri',
      next: 'İleri',
      finishTour: 'Turu bitir',
    },
    tourComplete: {
      label: 'Tur tamamlandı',
      description: "Başlamaya hazır mısın? Dashboard'a bir tık uzaktasın.",
      cta: "Dashboard'ı aç",
    },
    checklist: {
      sectionTitle: 'Başlangıç adımları',
      progress: (done, total) => `${done}/${total}`,
      footnote:
        'İşaretleyerek ilerle — bu manuel bir kontrol listesi (V1 simulation; Wave 2 gerçek backend sinyalleriyle otomatik algılar).',
    },
    skipLink: "Veya her şeyi atla, doğrudan dashboard'a git",
    footer: 'PEXIFY · özel yönetilen ticaret ekosistemi',
  },
}
